import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { studentLogin, saveSession } from '../../utils/api';
import LevavLogo from '../../sources/Logo/LEVAV Logo.png';

function StudentLogin() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      console.log('=== Login Attempt ===');
      console.log('Email:', email);
      
      const response = await studentLogin(email, password);
      
      console.log('=== Login Response ===');
      console.log('Session Token:', response.session_token);
      console.log('User ID:', response.user_id);
      console.log('=====================');
      
      // Save session token and user ID
      saveSession(response.session_token, response.user_id);
      
      // Navigate to student home page
      navigate('/student-home');
    } catch (err) {
      // Parse error message and map to translation keys
      let errorKey = 'Login.unknownError';
      const errorMsg = err.message?.toLowerCase() || '';
      
      if (errorMsg.includes('user') && (errorMsg.includes('not found') || errorMsg.includes('does not exist') || errorMsg.includes('not exist'))) {
        errorKey = 'Login.userNotFound';
      } else if (errorMsg.includes('password') || errorMsg.includes('incorrect') || errorMsg.includes('wrong')) {
        errorKey = 'Login.wrongPassword';
      } else if (errorMsg.includes('invalid') || errorMsg.includes('credentials')) {
        errorKey = 'Login.invalidCredentials';
      } else if (errorMsg.includes('server') || errorMsg.includes('500')) {
        errorKey = 'Login.serverError';
      } else if (errorMsg.includes('network') || errorMsg.includes('connection')) {
        errorKey = 'Login.networkError';
      }
      
      setError(t(errorKey));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        height: '100vh',
        width: '100vw',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #e8f4f8 0%, #f5e6f0 100%)',
        overflow: 'hidden',
        position: 'fixed',
        top: 0,
        left: 0,
      }}
    >
      <Container maxWidth="sm">
        <Paper
          elevation={3}
          sx={{
            p: 4,
            borderRadius: 3,
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
          }}
        >
          {/* Logo */}
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
            <img 
              src={LevavLogo} 
              alt="LEVAV Logo" 
              style={{ height: '60px', width: 'auto' }}
            />
          </Box>

          {/* Title */}
          <Typography
            variant="h4"
            component="h1"
            align="center"
            sx={{
              mb: 1,
              fontWeight: 600,
              color: '#4e7ba8',
            }}
          >
            {t('Login.assessmentLogin')}
          </Typography>

          <Typography
            variant="body2"
            align="center"
            sx={{ mb: 4, color: '#666' }}
          >
            {/* Login to access your mental health assessment */}
          </Typography>

          {/* Login Form */}
          <form onSubmit={handleLogin}>
            <TextField
              fullWidth
              label={t('Login.email')}
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={loading}
              sx={{ mb: 2 }}
              InputProps={{
                sx: { borderRadius: 2 }
              }}
            />

            <TextField
              fullWidth
              label={t('Login.password')}
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={loading}
              error={!!error}
              sx={{ mb: 0.5 }}
              InputProps={{
                sx: { borderRadius: 2 },
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      onMouseDown={(e) => e.preventDefault()}
                      edge="end"
                      disabled={loading}
                      sx={{ color: '#4e7ba8' }}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
            
            {/* Error Message */}
            {error && (
              <Typography
                variant="body2"
                sx={{
                  color: '#d32f2f',
                  mb: 2,
                  mt: 0.5,
                  fontSize: '0.875rem',
                }}
              >
                {error}
              </Typography>
            )}
            {!error && <Box sx={{ mb: 3 }} />}

            <Button
              fullWidth
              type="submit"
              variant="contained"
              disabled={loading}
              sx={{
                py: 1.5,
                borderRadius: 2,
                backgroundColor: '#65cb9a',
                textTransform: 'none',
                fontSize: '1.1rem',
                fontWeight: 500,
                '&:hover': {
                  backgroundColor: '#1a9f77',
                },
                '&:disabled': {
                  backgroundColor: '#ccc',
                },
              }}
            >
              {loading ? (
                <CircularProgress size={24} sx={{ color: 'white' }} />
              ) : (
                t('Login.login')
              )}
            </Button>
          </form>

          {/* Back to Home */}
          <Box sx={{ mt: 3, textAlign: 'center' }}>
            <Button
              onClick={() => navigate('/')}
              sx={{
                color: '#4e7ba8',
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: 'transparent',
                  textDecoration: 'underline',
                },
              }}
            >
              {t('Login.backToHome')}
            </Button>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
}

export default StudentLogin;
